/**
 * Reading right-panel state.
 *
 * Pure functions over the store's `byThreadKey` map, so they can be tested and
 * composed without React and reused by `useSyncExternalStore` subscribers
 * without allocating.
 *
 * The distinction these exist to keep straight is *selected* versus *visible*.
 * Hiding the panel deliberately keeps its surfaces and its selection, so
 * reopening returns you to what you were looking at — which means "what is
 * active" and "what should render" are different questions. The previous
 * URL-based model could not express "open but hidden" at all, so every caller
 * conflated them.
 */
import {
  EMPTY_THREAD_STATE,
  type RightPanelKind,
  type RightPanelSurface,
  type ThreadRightPanelState,
} from "./rightPanelSurface";

type ByThreadKey = Readonly<Record<string, ThreadRightPanelState>>;

/**
 * Returns the shared empty state — by identity — for a thread with none.
 * A fresh object per call would re-render every subscriber on every change.
 */
export function selectThreadRightPanelState(
  byThreadKey: ByThreadKey,
  threadKey: string | null,
): ThreadRightPanelState {
  if (!threadKey) return EMPTY_THREAD_STATE;
  return byThreadKey[threadKey] ?? EMPTY_THREAD_STATE;
}

/** The selected surface, whether or not the panel is currently showing. */
export function selectActiveSurface(
  byThreadKey: ByThreadKey,
  threadKey: string | null,
): RightPanelSurface | null {
  const state = selectThreadRightPanelState(byThreadKey, threadKey);
  if (!state.activeSurfaceId) return null;
  return state.surfaces.find((surface) => surface.id === state.activeSurfaceId) ?? null;
}

/** The surface that should actually render — `null` while the panel is hidden. */
export function selectVisibleSurface(
  byThreadKey: ByThreadKey,
  threadKey: string | null,
): RightPanelSurface | null {
  const state = selectThreadRightPanelState(byThreadKey, threadKey);
  if (!state.isOpen) return null;
  return selectActiveSurface(byThreadKey, threadKey);
}

/** Which kinds have at least one surface open, regardless of instance count. */
export function selectOpenKinds(
  byThreadKey: ByThreadKey,
  threadKey: string | null,
): ReadonlySet<RightPanelKind> {
  const state = selectThreadRightPanelState(byThreadKey, threadKey);
  return new Set(state.surfaces.map((surface) => surface.kind));
}

/**
 * Whether the panel is currently *showing* a surface of this kind. Callers
 * asking "is the diff up" must not get `true` from a collapsed panel.
 */
export function selectIsKindActive(
  byThreadKey: ByThreadKey,
  threadKey: string | null,
  kind: RightPanelKind,
): boolean {
  return selectVisibleSurface(byThreadKey, threadKey)?.kind === kind;
}
